<script setup>
import { formatBraindateWhen } from '#shared/utilities/braindate/format';
import { useBraindateLink } from '@/composables/useBraindateLink';

const props = defineProps({
    braindate: { type: Object, required: true },
    year: { type: Number, default: 2026 },
});

const when = computed(() => formatBraindateWhen(
    props.braindate.startDateTime,
    props.braindate.endDateTime,
    props.year,
));

// The annotation says the ENTIRE card links off, so the gate lives on the
// anchor. useBraindateLink carries the origin-checked SSO append and the
// logged-out login-modal hand-off, shared with the Explore Braindates button
// in braindates.vue.
const { href, target, handleClick } = useBraindateLink(() => props.braindate.url);

// Exported from the Figma pill (node 4530:105598) at its authored 16x10 box.
// Static public path — never `import` media, it blows the Worker memory limit.
const pillIcon = '/img/2026/braindates/braindate-pill-icon.svg';
</script>

<template>
    <a
        :href="href"
        :target="target"
        class="braindate-card group flex flex-col h-full rounded-[10px] p-[26px] lg:p-[36px] no-underline
            bg-white/85 backdrop-blur-[25px] border border-white/35
            transition-[background-color,border-color,box-shadow] duration-200
            hover:bg-white hover:border-transparent hover:shadow-[0_10px_30px_rgba(70,6,43,0.25)]"
        @click="handleClick"
    >
        <!-- 15px between the meta row and the title, per the card's inner stack
             (4530:105595). The 26px below is a MINIMUM gap to the CTA: paired with
             the CTA's mt-auto it never collapses, because auto margins resolve
             against the leftover space and margins don't collapse in a flex
             container. A short card gets exactly 26px; a card stretched to match
             a taller sibling gets 26px plus the slack. -->
        <div class="flex flex-col gap-[15px] mb-[26px]">
            <!-- Stacked on a narrow card, side by side once there's room. The card
                 is max-w-[85vw], so below roughly a 500px viewport its content box
                 can't hold the pill, the 18px gap and a one-line date — the date
                 wrapped to three lines and squeezed the pill until its own label
                 overflowed the fill. 520px is where the content box clears the two
                 laid out in a row. -->
            <!-- Stacked gap matches the session detail card (11px) so the two read the
                 same on mobile (Nick, UNB). The row gap above 520px is horizontal
                 spacing and keeps its own value. -->
            <div class="flex flex-col items-start gap-[11px] min-[520px]:flex-row min-[520px]:items-center min-[520px]:gap-[18px]">
                <!-- #af264d has no token in variables.scss — it is the pill fill,
                     the CTA colour and a gradient stop in this design, all literal.
                     shrink-0 so the pill can never be compressed narrower than its
                     own label, whatever the date does. -->
                <span class="braindate-card__pill shrink-0 inline-flex items-center gap-[10px] rounded-[25px] bg-[#af264d] text-white uppercase font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] px-[15px] py-[6px]">
                    <img :src="pillIcon" alt="" aria-hidden="true" class="w-[16px] h-[10px] shrink-0" />
                    Braindate
                </span>
                <span class="braindate-card__when font-hubspot-sans font-normal text-[14px] leading-[1.3] tracking-[0.21px] text-(--magenta)">{{ when }}</span>
            </div>

            <!-- tracking-normal, not "no class": typography.scss sets
                 `body { letter-spacing: 0.45px }`, which this inherited because
                 the title never declared its own. Every other element in the
                 card sets tracking explicitly, so it was the only one carrying
                 the body value (UNB-684). -->
            <h3 class="braindate-card__title font-hubspot-serif font-normal text-[30px] leading-[1.3] tracking-normal text-(--magenta) m-0">
                {{ braindate.name }}
            </h3>
        </div>

        <!-- No underline at rest in the comp (4530:105603) — it reads as a label.
             mt-auto pins it to the bottom of the card, so every card's CTA sits on
             the same baseline once the row has equalised heights.
             Hover uses the project's dotted-underline treatment (as in
             sessions/compact-session-card.vue): the decoration properties are
             declared up front and only the LINE toggles, so there is one hover
             utility rather than a group-hover per property. It is group-hover, not
             hover — the whole card is the anchor, so pointing anywhere on the card
             should light the CTA rather than only crossing those two words.
             On hover the CTA turns magenta with a dark-pink rule (UNB-683), so
             the decoration colour is set explicitly rather than inherited —
             currentColor would drag the underline to the magenta too. -->
        <span class="braindate-card__cta mt-auto font-hubspot-sans font-medium text-[13px] leading-none tracking-[1.04px] uppercase text-[#af264d]
            no-underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
            [text-decoration-skip-ink:auto] [text-underline-position:from-font]
            transition-colors duration-200
            group-hover:underline group-hover:text-(--magenta) group-hover:decoration-(--dark-pink)">
            Book Your Spot
        </span>

        <!-- Only when the anchor really opens a new tab, i.e. logged in. The
             announcement is the same one MessLink appends to an external link;
             without it the only cue is visual. No `rel`, matching MessLink —
             browsers imply noopener for target=_blank. -->
        <span v-if="target" class="sr-only">(opens in a new tab)</span>
    </a>
</template>
